import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { BsButton } from '@hotelplan/core.basis.bs-button';
import { formatPrice } from '@hotelplan/fdr.lib.format.format-price';
import { FdrImage } from '@hotelplan/fdr.regular.basis.fdr-image';
import { FdrProductOfferStatus } from '@hotelplan/supergraph-api';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import {
  ConfirmationModal,
  useConfirmation,
} from 'components/domain/confirmation';
import useAddOfferToActiveWishlist from 'fdr/components/candidate/fdr-wishlist/fdr-use-add-offer-to-active-wishlist';
import useRemoveOfferFromActiveWishlist from 'fdr/components/candidate/fdr-wishlist/fdr-use-remove-offer-from-active-wishlist';
import { TWishlistSelectedItems } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import FdrFlightSectionShort from 'fdr/components/local/fdr-flight-section/fdr-flight-section-short';
import { mapFdrOrlFlightToIFdrFlightSectionProps } from 'fdr/components/local/fdr-orl/fdr-orl.mappers';

export type WishlistOfferProduct = TWishlistSelectedItems[number];

export interface IFdrWishlistItemOverlay<
  T extends WishlistOfferProduct = WishlistOfferProduct
> {
  item: T;
  className?: string;
  onAccept?(item: T | null): void;
}

interface IWishlistNotAvailableOverlayProps {
  offerId: string;
  onApply?(): void;
  className?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FlightsWrapper = styled.div.attrs({
  className: 'flights',
})(({ theme: { FONT_SIZE } }) =>
  sx2CssThemeFn({
    display: 'flex',
    justifyContent: `space-evenly`,
    flexDirection: 'row',
    textAlign: 'left',
    mb: 2,
    '.flight-info': {
      mb: 3,
      '.departure-airport, .departure-date, .time-info': FONT_SIZE.MEDIUM,
    },
  })
);

const FlightInfo: React.FC<IFdrWishlistItemOverlay> = ({ item }) => {
  const [t] = useTranslation('frl');

  return (
    <FlightsWrapper>
      {item.trackedOffer.latest?.forwardFlight && (
        <div>
          <p>{t('frl:flight.outward')}</p>
          <FdrFlightSectionShort
            {...mapFdrOrlFlightToIFdrFlightSectionProps(
              item.trackedOffer.latest.forwardFlight
            )}
            className="flight-info"
          />
        </div>
      )}
      {item.trackedOffer.latest?.returnFlight && (
        <div>
          <p>{t('frl:flight.return')}</p>
          <FdrFlightSectionShort
            {...mapFdrOrlFlightToIFdrFlightSectionProps(
              item.trackedOffer.latest.returnFlight
            )}
            className="flight-info"
          />
        </div>
      )}
    </FlightsWrapper>
  );
};

const ImageWrapper = styled(FdrImage)({
  width: '100%',
  height: '16px',
  verticalAlign: 'middle',
});

const ButtonWrapper = styled(BsButton)(
  sx2CssThemeFn({
    px: [2, 4],
    py: [2, 3],
    fontSize: [2, '20px'],
  })
);

const Loader: React.FC = () => {
  return <ImageWrapper src="/images/spinner-button.svg" />;
};

const WishlistOverlayWrapper = styled.div(
  sx2CssThemeFn({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    p: [2, 4],
    textAlign: 'center',
    background: 'rgba(255, 255, 255, 0.94)',
    '.wishlist-overlay-title': {
      mx: '-5px',
      fontWeight: 'bold',
      marginBottom: 3,
    },
  })
);

const WishlistNotAvailableOverlay: React.FC<IWishlistNotAvailableOverlayProps> =
  props => {
    const { onApply, offerId, className } = props;
    const [t] = useTranslation(['frl', 'wishlist']);
    const [removeFromActiveWishlist] = useRemoveOfferFromActiveWishlist();

    const [, confirmationProps] = useConfirmation(removeFromActiveWishlist, {
      closeOnApply: true,
    });

    return (
      <WishlistOverlayWrapper className={className}>
        <div className="wishlist-overlay-title">
          {t('wishlist:overlay.notAvailable.title')}
        </div>
        <ConfirmationModal
          {...confirmationProps}
          title={t('wishlist:removeItem.confirmation.title')}
          question={t('wishlist:removeItem.confirmation.question')}
          onApply={() => {
            confirmationProps.onApply(offerId);
            onApply?.();
          }}
        />
      </WishlistOverlayWrapper>
    );
  };

const FdrWishlistItemOverlay = <T extends WishlistOfferProduct>({
  item,
  onAccept,
  className,
}: React.PropsWithChildren<IFdrWishlistItemOverlay<T>>) => {
  const [t] = useTranslation(['frl', 'wishlist']);

  const [addOffer, loading] = useAddOfferToActiveWishlist();

  const onAcceptChanges = async () => {
    if (loading || !item?.trackedOffer.latest?.id) return;

    const addedWishlistItem = await addOffer(item.trackedOffer.latest.id);

    if (!addedWishlistItem) return;

    onAccept?.({ ...item, trackedOffer: addedWishlistItem.trackedOffer });
  };

  const notAvailableOverlay = (
    <WishlistNotAvailableOverlay
      offerId={item.trackedOffer.original.id}
      onApply={() => onAccept?.(null)}
      className={className}
    />
  );

  if (item.trackedOffer.modified) {
    return null;
  }

  if (item.trackedOffer.modified === null) {
    return notAvailableOverlay;
  }

  switch (item.trackedOffer.status) {
    default:
    case FdrProductOfferStatus.Available:
      return null;
    case FdrProductOfferStatus.NotAvailable:
      return notAvailableOverlay;
    case FdrProductOfferStatus.FlightChanged:
      return (
        <WishlistOverlayWrapper className={className}>
          <div className="wishlist-overlay-title">
            {t('wishlist:overlay.flightChanged.title')}
          </div>
          <FlightInfo item={item} />
          <ButtonWrapper onClick={onAcceptChanges}>
            {loading ? <Loader /> : t('wishlist:overlay.acceptChanges.button')}
          </ButtonWrapper>
        </WishlistOverlayWrapper>
      );
    case FdrProductOfferStatus.PriceChanged:
      return (
        <WishlistOverlayWrapper className={className}>
          <div className="wishlist-overlay-title">
            {t('wishlist:overlay.priceChanged.title', {
              price: item.trackedOffer.latest.priceTotal
                ? formatPrice(item.trackedOffer.latest.priceTotal)
                : 0,
            })}
          </div>
          <ButtonWrapper onClick={onAcceptChanges}>
            {loading ? <Loader /> : t('wishlist:overlay.acceptChanges.button')}
          </ButtonWrapper>
        </WishlistOverlayWrapper>
      );
    case FdrProductOfferStatus.FlightAndPriceChanged:
      return (
        <WishlistOverlayWrapper className={className}>
          <div className="wishlist-overlay-title">
            {t('wishlist:overlay.priceChanged.title', {
              price: item.trackedOffer.latest.priceTotal
                ? formatPrice(item.trackedOffer.latest.priceTotal)
                : 0,
            })}
          </div>
          <div className="wishlist-overlay-title">
            {t('wishlist:overlay.flightChanged.title')}
          </div>
          <FlightInfo item={item} />
          <ButtonWrapper onClick={onAcceptChanges}>
            {loading ? <Loader /> : t('wishlist:overlay.acceptChanges.button')}
          </ButtonWrapper>
        </WishlistOverlayWrapper>
      );
  }
};

export default FdrWishlistItemOverlay;
